import React, { useMemo, useState } from 'react'
import styles from '../IntroStep.module.scss'

// Illustrative data only — this panel is a picture of the workflow, not the
// workflow. Keeping it self-contained means the homepage never waits on a
// parser or a chart bundle to paint.
const DATASETS = [
  {
    chip: 'arr-by-month.csv',
    caption: 'Monthly recurring revenue, 9 months',
    raw: [
      'month,mrr,churn',
      '2026-01,41200,2.1',
      '2026-02,44810,1.8',
      '2026-03,43990,2.4',
      '2026-04,51300,1.6',
      '2026-05,58740,1.4',
    ],
    more: '…203 more rows',
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'],
    values: [41.2, 44.8, 44.0, 51.3, 58.7, 61.2, 66.4, 71.9, 78.3],
  },
  {
    chip: 'sensor-drift.tsv',
    caption: 'Receiver drift across 12 windows',
    raw: [
      'window\tdrift\tsat',
      '0\t12.4\t7',
      '1\t18.9\t7',
      '2\t31.2\t6',
      '3\t27.5\t8',
      '4\t44.1\t5',
    ],
    more: '…21,600 more rows',
    labels: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'],
    values: [12.4, 18.9, 31.2, 27.5, 44.1, 39.8, 52.6, 47.2, 61.4, 55.9, 68.1, 49.3],
  },
  {
    // Periods only. Mixing a total into the same bar series as its own
    // components sets the y-axis with a number that is not comparable to its
    // neighbours — a bad look on a page selling charting judgement.
    chip: 'shots-game5.csv',
    caption: 'Points by period, both teams',
    raw: [
      'player,team,shot_distance,pts',
      'Brunson,NYK,4.1,9',
      'Wembanyama,SAS,22.8,11',
      'Brunson,NYK,17.5,7',
      'Hart,NYK,3.2,6',
    ],
    more: '…412 more rows',
    labels: ['Q1', 'Q2', 'Q3', 'Q4', 'OT'],
    values: [26, 31, 24, 33, 9],
  },
]

const CHART_MODES = [
  { id: 'bars', label: 'Bars' },
  { id: 'line', label: 'Line' },
  { id: 'area', label: 'Area' },
]

const ACCENT = { base: '#4fb6c6', soft: 'rgba(79,182,198,.22)', dim: 'rgba(79,182,198,.5)' }

const W = 620
const H = 300
const PAD = { l: 46, r: 8, t: 16, b: 40 }

function buildGeometry(values) {
  const plotW = W - PAD.l - PAD.r
  const plotH = H - PAD.t - PAD.b
  const max = Math.max(...values) * 1.12
  const step = plotW / values.length
  const yOf = (v) => PAD.t + plotH * (1 - v / max)
  const xOf = (i) => PAD.l + step * (i + 0.5)
  const baseY = PAD.t + plotH

  // Highlight the peak rather than the last value — "last" only reads as
  // meaningful on a time series, and two of the three datasets are not one.
  const peak = values.indexOf(Math.max(...values))
  const barW = step * 0.54

  const points = values.map((v, i) => `${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`)

  return {
    max,
    baseY,
    bars: values.map((v, i) => ({
      x: (xOf(i) - barW / 2).toFixed(1),
      y: yOf(v).toFixed(1),
      w: barW.toFixed(1),
      h: (baseY - yOf(v)).toFixed(1),
      fill: i === peak ? ACCENT.base : ACCENT.dim,
    })),
    points,
    linePts: points.join(' '),
    areaPts: [
      `${xOf(0).toFixed(1)},${baseY}`,
      ...points,
      `${xOf(values.length - 1).toFixed(1)},${baseY}`,
    ].join(' '),
    grid: [0, 0.25, 0.5, 0.75, 1].map((f) => ({
      y: (PAD.t + plotH * f).toFixed(1),
      topPct: `${(((PAD.t + plotH * f) / H) * 100).toFixed(2)}%`,
      label: String(Math.round(max * (1 - f))),
    })),
    xOf,
  }
}

export default function HomeDemo() {
  const [dsIndex, setDsIndex] = useState(0)
  const [mode, setMode] = useState('bars')

  const dataset = DATASETS[dsIndex]
  const geo = useMemo(() => buildGeometry(dataset.values), [dataset])

  const xLabels = dataset.labels
    .slice(0, dataset.values.length)
    .map((label, i) => ({ label, leftPct: `${((geo.xOf(i) / W) * 100).toFixed(2)}%` }))

  return (
    <section className={styles.demoSection} aria-label="Workflow preview">
      <div className={styles.demoCard}>
        <div className={styles.demoToolbar}>
          <div className={styles.demoChips}>
            {DATASETS.map((d, i) => (
              <button
                key={d.chip}
                type="button"
                className={i === dsIndex ? styles.demoChipOn : styles.demoChip}
                aria-pressed={i === dsIndex}
                onClick={() => setDsIndex(i)}
              >
                {d.chip}
              </button>
            ))}
          </div>
          <div className={styles.demoModes} role="group" aria-label="Chart type">
            {CHART_MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                className={m.id === mode ? styles.demoModeOn : styles.demoMode}
                aria-pressed={m.id === mode}
                onClick={() => setMode(m.id)}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.demoBody}>
          <div className={styles.demoRaw}>
            {dataset.raw.map((line, i) => (
              <div
                key={line}
                className={styles.demoRawLine}
                style={i === 0 ? { color: ACCENT.base } : undefined}
              >
                {line}
              </div>
            ))}
            <div className={styles.demoRawMore}>{dataset.more}</div>
          </div>

          <div className={styles.demoChartPane}>
            <div className={styles.demoChartWrap}>
              <svg
                viewBox={`0 0 ${W} ${H}`}
                className={styles.demoSvg}
                role="img"
                aria-label={`${dataset.caption}, shown as a ${mode} chart`}
              >
                {geo.grid.map((g) => (
                  <line
                    key={g.y}
                    x1={PAD.l}
                    y1={g.y}
                    x2={W - PAD.r}
                    y2={g.y}
                    stroke="rgba(245,234,216,.1)"
                    strokeWidth="1"
                  />
                ))}

                {mode === 'bars' &&
                  geo.bars.map((b) => (
                    <rect key={b.x} x={b.x} y={b.y} width={b.w} height={b.h} rx="5" fill={b.fill} />
                  ))}

                {mode === 'area' && (
                  <polygon points={geo.areaPts} fill={ACCENT.soft} />
                )}

                {(mode === 'line' || mode === 'area') && (
                  <polyline
                    points={geo.linePts}
                    fill="none"
                    stroke={ACCENT.base}
                    strokeWidth="3"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                )}

                {mode === 'line' &&
                  geo.points.map((p) => {
                    const [cx, cy] = p.split(',')
                    return (
                      <circle
                        key={p}
                        cx={cx}
                        cy={cy}
                        r="4.5"
                        fill="#1b1917"
                        stroke={ACCENT.base}
                        strokeWidth="2.75"
                      />
                    )
                  })}
              </svg>

              {geo.grid.map((g) => (
                <div key={g.y} className={styles.demoYLabel} style={{ top: g.topPct }}>
                  {g.label}
                </div>
              ))}
              {xLabels.map((x) => (
                <div key={x.label} className={styles.demoXLabel} style={{ left: x.leftPct }}>
                  {x.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.demoCaptionRow}>
        <p className={styles.demoCaption}>
          {dataset.caption} — parsed, typed and drawn in this tab.
        </p>
        <ol className={styles.demoSteps}>
          {['Data', 'Map', 'Style', 'Export'].map((label, i) => (
            <li key={label}>
              <span className={styles.demoStepNum}>{i + 1}</span> {label}
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}
